/**
 * backgroundQueryModalParts.jsx — presentational sub-components for BackgroundQueryModal.
 * Extracted verbatim from components/BackgroundQueryModal.jsx (god-file decomposition).
 * AnimatedProgressBar, ElapsedTimeDisplay, and GradientViewButton are exported for
 * use by the main component; VIEW_BUTTON_GRADIENTS, progressStyles, and
 * gradientBtnStyles are internal helpers used within this file.
 */

import { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { isMobile, SCREEN_WIDTH } from '../utils/responsive';

export const AnimatedProgressBar = ({ progress, isProcessing }) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(0.3)).current;
  
  // Continuous shimmer animation
  useEffect(() => {
    if (isProcessing) {
      const shimmer = Animated.loop(
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1500,
          easing: Easing.linear,
          useNativeDriver: true
        })
      );
      shimmer.start();
      
      // Also pulse the opacity when progress is low
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          }),
          Animated.timing(pulseAnim, {
            toValue: 0.5,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          })
        ])
      );
      pulse.start();
      
      return () => {
        shimmer.stop();
        pulse.stop();
      };
    }
  }, [isProcessing]);
  
  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-100, 200]
  });
  
  // When progress is very low, show indeterminate animation
  const effectiveProgress = progress < 10 ? Math.max(progress, 30) : progress;
  
  return (
    <View style={progressStyles.container}>
      <View style={progressStyles.bar}>
        {/* Base progress fill */}
        <Animated.View 
          style={[
            progressStyles.fill, 
            { 
              width: `${effectiveProgress}%`,
              opacity: progress < 10 ? pulseAnim : 1
            }
          ]}
        >
          <LinearGradient
            colors={['#8b5cf6', '#a78bfa', '#c4b5fd']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
        
        {/* Shimmer overlay */}
        {isProcessing && (
          <Animated.View
            style={[
              progressStyles.shimmer,
              {
                transform: [{ translateX: shimmerTranslate }]
              }
            ]}
          >
            <LinearGradient
              colors={['transparent', 'rgba(255,255,255,0.3)', 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
        )}
      </View>
    </View>
  );
};

const progressStyles = StyleSheet.create({
  container: {
    marginTop: 4,
  },
  bar: {
    height: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
    position: 'relative',
  },
  fill: {
    height: '100%',
    borderRadius: 2,
    overflow: 'hidden',
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 100,
  }
});

// Elapsed time display component with live updating
export const ElapsedTimeDisplay = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    
    const updateElapsed = () => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <Text style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' }}>
      {formatTime(elapsed)}
    </Text>
  );
};

// Detect mobile screen

// Gradient view button — purple / green / blue based on query type
const VIEW_BUTTON_GRADIENTS = {
  sgp:      { colors: ['#7c3aed', '#a78bfa'], border: 'rgba(139,92,246,0.5)' },
  live:     { colors: ['#059669', '#34d399'], border: 'rgba(16,185,129,0.5)' },
  straight: { colors: ['#1d4ed8', '#60a5fa'], border: 'rgba(59,130,246,0.5)' },
  default:  { colors: ['#5b21b6', '#8b5cf6'], border: 'rgba(139,92,246,0.3)' },
};

export const GradientViewButton = ({ onPress, isSGP, isLive }) => {
  const key = isLive ? 'live' : isSGP ? 'sgp' : 'straight';
  const { colors, border } = VIEW_BUTTON_GRADIENTS[key] || VIEW_BUTTON_GRADIENTS.default;
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[gradientBtnStyles.wrapper, { borderColor: border }]}
      activeOpacity={0.8}
    >
      <LinearGradient
        colors={colors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={gradientBtnStyles.inner}
      >
        <Text style={gradientBtnStyles.text}>View</Text>
        <Ionicons name="arrow-forward" size={14} color="#fff" />
      </LinearGradient>
    </TouchableOpacity>
  );
};

const gradientBtnStyles = StyleSheet.create({
  wrapper: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 7,
    paddingHorizontal: 14,
  },
  text: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
});
